/**
 * Forum Sidebar Component
 * 
 * Displays sidebar information with active users, recent activity, and quick stats
 */

import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import AnimatedBadge, { BadgeStyle, BadgeAnimation } from '@/components/common/AnimatedBadge'
import { Users, Activity, Star, TrendingUp, Award } from 'lucide-react'
import { useAvatar } from '@/hooks/useAvatar'
import { useTranslation } from 'react-i18next';

interface OnlineUser {
  id: string
  name: string
  nick?: string
  avatar?: string
  badge?: string
  badges?: Array<{
    id: string
    name: string
    icon: string
    color: string
    style?: string
    animation?: string
  }>
}

interface RecentActivity {
  id: string
  type: 'new_topic' | 'new_post' | 'reaction'
  user: string
  action: string
  time: string
}

interface ForumBadge {
  id: string
  name: string
  description: string
  icon: string
  color: string
  type: string
  style?: string
  animation?: string
  postsRequired?: number
  topicsRequired?: number
  reactionsRequired?: number
}

interface BadgeEarner {
  id: string
  playerName: string
  badgeName: string
  badgeIcon: string
  badgeColor: string
  badgeStyle?: string
  badgeAnimation?: string
  timeAgo: string
}

interface ForumSidebarProps {
  badges?: ForumBadge[]
  badgeEarners?: BadgeEarner[]
  onlineUsers?: OnlineUser[]
  recentActivity?: RecentActivity[]
  topContributors?: Array<{ 
    name: string; 
    posts: number;
    badges?: Array<{
      id: string;
      name: string;
      icon: string;
      color: string;
      style?: string;
      animation?: string;
    }>;
  }>
}

export default function ForumSidebar({
  badges = [],
  badgeEarners = [],
  onlineUsers = [],
  recentActivity = [],
  topContributors = [],
}: ForumSidebarProps) {
  const { t } = useTranslation();
  const { getAvatarUrl } = useAvatar()

  return (
    <div className="space-y-6">
      {/* Forum Badges */}
      <Card className="border-card-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Award className="w-4 h-4 text-primary" />
            </div>
            <span>{t('store.forum.badges')}</span>
            <Badge variant="secondary" className="ml-auto">
              {badges.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {badges.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              {t('store.forum.badgesEmpty')}
            </p>
          ) : (
            <div className="space-y-3">
              {badges.slice(0, 5).map((badge) => (
                <div
                  key={badge.id}
                  className="p-3 rounded-lg bg-card-subtle hover:bg-accent/5 transition-colors border border-border"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <AnimatedBadge
                          name={badge.name}
                          icon={badge.icon}
                          style={(badge.style as BadgeStyle) || 'DEFAULT'}
                          animation={(badge.animation as BadgeAnimation) || 'NONE'}
                        />
                        <Badge variant="outline" className="text-xs h-5">
                          {t(badge.type === 'SYSTEM' ? 'store.forum.badgeAuto' : 'store.forum.badgeCustom')}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {badge.description}
                      </p>
                      {(badge.postsRequired || badge.topicsRequired || badge.reactionsRequired) && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {badge.topicsRequired && (
                            <Badge variant="secondary" className="text-xs">
                              📝 {badge.topicsRequired} tópicos
                            </Badge>
                          )}
                          {badge.postsRequired && (
                            <Badge variant="secondary" className="text-xs">
                              💬 {badge.postsRequired} posts
                            </Badge>
                          )}
                          {badge.reactionsRequired && (
                            <Badge variant="secondary" className="text-xs">
                              ❤️ {badge.reactionsRequired} reações
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Badge Earners */}
      <Card className="border-card-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Star className="w-4 h-4 text-primary" />
            </div>
            <span>{t('store.forum.recentBadges')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {badgeEarners.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              {t('store.forum.recentBadgesEmpty')}
            </p>
          ) : (
            <div className="space-y-3">
              {badgeEarners.slice(0, 5).map((earner) => (
                <div
                  key={earner.id}
                  className="p-3 rounded-lg bg-card-subtle hover:bg-accent/5 transition-colors border border-border"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate mb-2">
                        {earner.playerName}
                      </p>
                      <AnimatedBadge
                        name={earner.badgeName}
                        icon={earner.badgeIcon}
                        style={(earner.badgeStyle as BadgeStyle) || 'DEFAULT'}
                        animation={(earner.badgeAnimation as BadgeAnimation) || 'NONE'}
                      />
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {earner.timeAgo}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Online Users */}
      <Card className="border-card-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Users className="w-4 h-4 text-primary" />
            </div>
            <span>{t('store.forum.onlineUsers')}</span>
            <Badge variant="secondary" className="ml-auto">
              {onlineUsers.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {onlineUsers.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              {t('store.forum.onlineUsersEmpty')}
            </p>
          ) : (
            <div className="space-y-2">
              {onlineUsers.slice(0, 10).map((user) => (
                <div
                  key={user.id}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-accent/5 transition-colors"
                >
                  <div className="relative">
                    <img
                      src={getAvatarUrl(user.nick || user.name, 64)}
                      alt={user.name || t('store.forum.player')}
                      className="w-8 h-8 rounded-full"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                        const fallback = document.createElement('div')
                        fallback.className = 'w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold text-sm'
                        fallback.textContent = user.name ? user.name[0].toUpperCase() : '?'
                        target.parentElement!.insertBefore(fallback, target)
                      }}
                    />
                    <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-accent rounded-full border-2 border-card" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {user.name || t('store.forum.player')}
                    </p>
                    {user.badges && user.badges.length > 0 ? (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {user.badges.map((badge) => (
                          <AnimatedBadge
                            key={badge.id}
                            name={badge.name}
                            icon={badge.icon}
                            style={(badge.style as BadgeStyle) || 'DEFAULT'}
                            animation={(badge.animation as BadgeAnimation) || 'NONE'}
                            className="text-[10px] scale-75 origin-left"
                          />
                        ))}
                      </div>
                    ) : user.badge ? (
                      <p className="text-xs text-muted-foreground">{user.badge}</p>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card className="border-card-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Activity className="w-4 h-4 text-primary" />
            </div>
            <span>{t('store.forum.recentActivity')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentActivity.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              {t('store.forum.recentActivityEmpty')}
            </p>
          ) : (
            <div className="space-y-3">
              {recentActivity.slice(0, 5).map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-start gap-2 text-sm pb-3 border-b border-border last:border-0"
                >
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-1.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-foreground">
                      <span className="font-medium">{activity.user}</span>{' '}
                      {activity.action}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {activity.time}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Contributors */}
      <Card className="border-card-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Star className="w-4 h-4 text-primary" />
            </div>
            <span>{t('store.forum.topContributors')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {topContributors.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              {t('store.forum.topContributorsEmpty')}
            </p>
          ) : (
            <div className="space-y-3">
              {topContributors.slice(0, 5).map((contributor, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-accent/5 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-warning to-warning/60 flex items-center justify-center text-white font-bold text-sm">
                    #{index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {contributor.name}
                    </p>
                    {contributor.badges && contributor.badges.length > 0 ? (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {contributor.badges.map((badge) => (
                          <AnimatedBadge
                            key={badge.id}
                            name={badge.name}
                            icon={badge.icon}
                            style={(badge.style as BadgeStyle) || 'DEFAULT'}
                            animation={(badge.animation as BadgeAnimation) || 'NONE'}
                            className="text-[10px] scale-75 origin-left"
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        {contributor.posts} posts
                      </p>
                    )}
                  </div>
                  <TrendingUp className="w-4 h-4 text-primary flex-shrink-0" />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
